import { useEffect, useRef } from 'react';

const AboutSection = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
            section.querySelectorAll('.reveal-zoom').forEach((el) => {
              el.classList.add('revealed-zoom');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="sobre"
      ref={sectionRef}
      className="relative bg-background-50 py-20 md:py-28 overflow-hidden"
    >
      {/* Subtle organic decorative elements */}
      {/* Large soft leaf shape - top right */}
      <div className="absolute top-[-8%] right-[-6%] w-[340px] h-[340px] md:w-[500px] md:h-[500px] rounded-full bg-gradient-to-br from-primary-100/25 via-primary-50/15 to-primary-100/10 opacity-70 animate-blob" />
      {/* Grain-like dots - bottom left */}
      <div className="absolute bottom-[15%] left-[-3%] w-[180px] h-[180px] md:w-[260px] md:h-[260px] rounded-full bg-gradient-to-tr from-secondary-100/30 via-accent-50/20 to-secondary-50/15 opacity-60 animate-float-reverse" />
      {/* Small leaf accent - mid right */}
      <div className="absolute top-[45%] right-[-4%] w-[120px] h-[120px] md:w-[180px] md:h-[180px] rounded-full bg-gradient-to-b from-primary-50/30 to-secondary-100/25 opacity-50 animate-float" />
      {/* Tiny grain dots scattered */}
      <div className="absolute top-[25%] left-[10%] w-3 h-3 md:w-4 md:h-4 rounded-full bg-primary-200/30" />
      <div className="absolute top-[60%] right-[25%] w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-accent-200/25" />
      <div className="absolute bottom-[30%] left-[38%] w-2 h-2 md:w-3 md:h-3 rounded-full bg-secondary-300/20" />
      <div className="absolute top-[35%] right-[8%] w-3.5 h-3.5 md:w-5 md:h-5 rounded-full bg-primary-200/25" />
      <div className="absolute bottom-[20%] right-[12%] w-2 h-2 md:w-3 md:h-3 rounded-full bg-accent-100/30" />

      {/* Subtle curved line - organic leaf vein */}
      <svg className="absolute top-0 right-0 w-[280px] h-[280px] md:w-[400px] md:h-[400px] opacity-[0.06]" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M400 0C350 80 340 150 280 200C220 250 180 220 120 280C80 320 40 310 0 360" stroke="oklch(var(--primary-500))" strokeWidth="1.5" />
        <path d="M400 0C360 60 310 130 260 170C210 210 170 190 140 230C110 270 70 290 40 340" stroke="oklch(var(--primary-500))" strokeWidth="1" />
        <path d="M400 0C370 40 330 100 290 140C250 180 220 170 180 200C140 230 100 260 60 300" stroke="oklch(var(--primary-500))" strokeWidth="0.8" />
      </svg>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left - Image with zoom-in animation */}
            <div className="w-full lg:w-[48%]">
              <div className="reveal-zoom relative overflow-hidden rounded-[2rem] aspect-[4/5] max-w-[480px] mx-auto lg:max-w-none">
                <img
                  src="https://storage.readdy-site.link/project_files/46b11d92-1101-4f92-aa67-f7a4033739d8/ff0b48e2-58d8-4143-97d0-18c7351be4f4_compressed_Imagem-1-gerada-2.webp"
                  alt="Sanduíches naturais frescos da Naturalle"
                  className="w-full h-full object-cover"
                />
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
              </div>
            </div>

            {/* Right - Content */}
            <div className="w-full lg:w-[52%]">
              {/* Badge */}
              <div className="reveal reveal-delay-1 inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-accent-50 border border-accent-200/50 mb-6">
                <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-soft" />
                <span className="font-label text-xs md:text-sm font-semibold text-accent-700 tracking-wide uppercase">
                  Há mais de 24 anos
                </span>
              </div>

              {/* Title */}
              <h2 className="reveal reveal-delay-2 font-display text-3xl sm:text-4xl md:text-[42px] text-foreground-900 leading-[1.15] mb-6 text-balance">
                Comida de verdade,
                <br />
                <span className="text-accent-600">feita à mão em Fortaleza</span>
              </h2>

              {/* Institutional text */}
              <div className="reveal reveal-delay-3 space-y-4 mb-10">
                <p className="font-body text-sm md:text-base text-foreground-500 leading-relaxed">
                  Tudo começou numa cozinha simples aqui em Fortaleza, com uma aposta ousada: provar que
                  comida prática também pode ser comida de verdade. De lá pra cá, a Naturalle entrou na
                  rotina de milhares de cearenses — e a gente nunca abriu mão do jeito artesanal de fazer.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-500 leading-relaxed">
                  Todos os dias a produção é feita do zero: pão macio, recheios generosos e ingredientes
                  escolhidos a dedo. Cada sanduíche sai embalado a vácuo, o que preserva o sabor e garante
                  que ele chegue até você com o mesmo frescor de quando saiu da nossa cozinha.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-500 leading-relaxed">
                  Depois de mais de 24 anos, seguimos sendo uma empresa cearense movida pela mesma promessa
                  do primeiro dia: praticidade sem abrir mão de sabor, frescor e cuidado em cada detalhe.
                </p>
              </div>

              {/* Feature cards */}
              <div className="reveal reveal-delay-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="group p-5 rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200/70 transition-all duration-400">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-leaf-line text-primary-500 text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-1.5 whitespace-nowrap">
                    Ingredientes Frescos
                  </h4>
                  <p className="font-body text-xs text-foreground-400 leading-relaxed">
                    Selecionados diariamente para garantir mais sabor e qualidade.
                  </p>
                </div>

                <div className="group p-5 rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200/70 transition-all duration-400">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-shield-check-line text-secondary-500 text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-1.5 whitespace-nowrap">
                    Embalagem a Vácuo
                  </h4>
                  <p className="font-body text-xs text-foreground-400 leading-relaxed">
                    Mais segurança, frescor e maior conservação dos alimentos.
                  </p>
                </div>

                <div className="group p-5 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-200/70 transition-all duration-400">
                  <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-star-line text-accent-500 text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-1.5 whitespace-nowrap">
                    24 Anos de Tradição
                  </h4>
                  <p className="font-body text-xs text-foreground-400 leading-relaxed">
                    Uma marca consolidada e reconhecida pela confiança de seus clientes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;